import { useEffect, useMemo, useState } from "react";
import { UIButton } from "@src/components/UIButton";
import UIModal from "@src/components/UIModal/UIModal";
import AppSelectDropdown from "@src/components/AppSelectDropdown";
import {
  JingleOption,
  useVisibleJinglesQuery,
} from "@src/queries/jingleQueries";
import { IPlaylistRecord } from "@src/types/playlist";
import Spinner from "@src/components/Spinner";
import { useForm, Controller } from "react-hook-form";

type SelectJingleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (jingleDocumentId: string | null) => void;
  loading?: boolean;
  playlistData: IPlaylistRecord | null;
  /** The show's own jingle, which is never offered as a manual pick. */
  showJingleId?: string | null;
};

type JingleFormData = {
  jingleId: string;
};

const SelectJingleModal: React.FC<SelectJingleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
  playlistData,
  showJingleId,
}) => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const {
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<JingleFormData>({
    defaultValues: { jingleId: playlistData?.jingle?.documentId ?? "" },
  });

  const currentJingleId = watch("jingleId");

  // Reset form when modal opens or playlist jingle changes
  useEffect(() => {
    reset({ jingleId: playlistData?.jingle?.documentId ?? "" });
  }, [playlistData?.jingle?.documentId, isOpen]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchText]);

  const { data: jingles, isFetching } = useVisibleJinglesQuery(
    debouncedSearch,
    isOpen,
    showJingleId
  );

  // Keep the already-assigned jingle in the list even when the query leaves it
  // out — because it is now `visible: false`, or because it is the show's own
  // jingle — so its title still renders as the current selection. It can stay
  // assigned; it just isn't offered as a fresh option.
  const options = useMemo<JingleOption[]>(() => {
    const list = jingles ?? [];
    const assigned = playlistData?.jingle;
    if (
      assigned &&
      !list.some((j: JingleOption) => j.documentId === assigned.documentId)
    ) {
      return [...list, assigned];
    }
    return list;
  }, [jingles, playlistData?.jingle]);

  const originalJingleId = playlistData?.jingle?.documentId ?? "";
  const hasChanged = currentJingleId !== originalJingleId;

  const handleClose = () => {
    reset({ jingleId: originalJingleId });
    setSearchText("");
    onClose();
  };

  return (
    <UIModal
      modalStyles={{ width: 380 }}
      onClose={handleClose}
      isOpen={isOpen}
      headerStyles={{ textAlign: "center" }}
      title="Select Jingle"
      actions={[
        <UIButton key="cancel" onClick={handleClose} variant="secondaryOutline">
          Cancel
        </UIButton>,
        <UIButton
          key="save"
          onClick={() => onConfirm(currentJingleId || null)}
          variant="primary"
          disabled={!hasChanged || loading}
        >
          {loading ? <Spinner small /> : "Save"}
        </UIButton>,
      ]}
    >
      <div style={{ padding: "0 4px 8px" }}>
        <Controller
          name="jingleId"
          control={control}
          render={({ field }) => (
            <AppSelectDropdown
              label="Jingle"
              name={field.name}
              control={control}
              errors={errors}
              placeholder="Search and select a jingle…"
              options={options}
              labelAccessor="title"
              valueAccessor="documentId"
              value={field.value}
              searching={isFetching}
              searchText={searchText}
              setSearchText={setSearchText}
              onChange={(val: string) => field.onChange(val)}
              resetValue={() => field.onChange("")}
            />
          )}
        />
      </div>
    </UIModal>
  );
};

export default SelectJingleModal;
